import { FC, SVGProps, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  OwnershipType,
  ownershipValueToTitle,
} from '~domains/partner-application/entities/application';
import {
  OwnershipFormValues,
  ownershipShema,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Chips } from '@breezeventures/ui-kit/chips/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { zodResolver } from '@hookform/resolvers/zod';

import CorporationIcon from './icons/corporation.svg?react';
import PartnershipIcon from './icons/partnership.svg?react';
import SoleIcon from './icons/sole.svg?react';

const ownershipTypes: Array<{
  value: OwnershipType;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-restricted-types
  icon: FC<SVGProps<SVGSVGElement>>;
}> = [
  {
    value: 'INDIVIDUAL',
    title: ownershipValueToTitle['INDIVIDUAL'],
    icon: SoleIcon,
  },
  {
    value: 'PARTNERSHIP',
    title: ownershipValueToTitle['PARTNERSHIP'],
    icon: PartnershipIcon,
  },
  {
    value: 'CORPORATION',
    title: ownershipValueToTitle['CORPORATION'],
    icon: CorporationIcon,
  },
];

type OwnershipProps = {
  initialValues?: OwnershipFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: OwnershipFormValues) => Promise<void>;
};

export const Ownership = ({
  initialValues,
  onBack,
  onClose,
  onSubmit,
}: OwnershipProps) => {
  const methods = useForm<OwnershipFormValues>({
    resolver: zodResolver(ownershipShema),
    mode: 'all',
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  const handleSubmit = async (values: OwnershipFormValues) => {
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <Typography.Title size="s" className="py-6">
          What is your business type?
        </Typography.Title>
        <Controller
          name="type"
          control={methods.control}
          render={(fieldProps) => (
            <>
              <Chips
                {...fieldProps}
                type="single"
                variant="outlined"
                size="l"
                width="full"
                className={`grid grid-cols-2 gap-3`}
              >
                {ownershipTypes.map((option) => {
                  const Icon = option.icon;

                  return (
                    <Chips.Item
                      key={option.value}
                      value={option.value}
                      className={`flex h-auto! flex-col items-start p-3!`}
                    >
                      <div
                        className={`
                          mb-9 flex h-10 w-10 items-center justify-center
                          rounded-full bg-accent-1 text-white
                        `}
                      >
                        <Icon />
                      </div>
                      <Typography.Text size="m" className="text-start">
                        {option.title}
                      </Typography.Text>
                    </Chips.Item>
                  );
                })}
              </Chips>
              <div
                className={`
                  overflow-hidden transition-all duration-300 ease-in-out
                  ${
                    fieldProps.fieldState?.error
                      ? 'h-full opacity-100'
                      : `h-0 opacity-0`
                  }
                `}
              >
                <Typography.Text
                  size="xs"
                  color="negative"
                  className="pt-2 pl-2"
                >
                  {fieldProps.fieldState?.error?.message}
                </Typography.Text>
              </div>
            </>
          )}
        />
      </Screen>
    </form>
  );
};
